import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import { TYPES as ImagesTYPES } from './modules/images/iocTypes';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { ViewsService } from './Views.service';
import type { IBaseViewModel, Location } from './interfaces';
import { ViewPortKind, type IViewItemsViewModelController } from './interfaces';
import type { ImagesService } from './modules/images/Images.service';
import type { ViewController } from './View.controller';
import type { ILocatable } from '@/engine/PlanningAlgorithm/DataTypes';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { Vector3 } from 'three';
import type { SliceType } from './modules/images/interfaces';
import type { Type } from '@/engine/DataFlow/Type';
import { coronalOrientationFromDirection } from '@/engine/PlanningAlgorithm/Helpers';
import { AngleModel } from './modules/indications/angle/Angle.model';
import { DistanceModel } from './modules/indications/distance/Distance.model';
import { AnnotationModel } from './modules/indications/annotation/Annotation.model';
import type { IIndicationModel } from './modules/indications/interfaces';
import type * as THREE from 'three';
import type { IValue } from '../DataFlow/Graph';
import type { ValueConstructor } from '../DataFlow/DataFlow';

@injectable()
export class ViewItemsViewModelController implements IViewItemsViewModelController {
  public readonly _viewController: ViewController;

  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  public constructor(
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService
  ) {
    this._viewController = viewController;
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
  }

  public setMeasurementVisibility(descriptor: string, visible: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const measurement = this._viewsService
      .currentView!.getMeasurements()
      .find((meas) => meas.descriptor === descriptor);
    if (measurement) {
      measurement.setVisibility(visible);
    }
  }

  public setMeasurementHighlighting(descriptor: string, highlighted: boolean): void {
    const measurement = this._viewsService.currentView
      ?.getMeasurements()
      .find((meas) => meas.descriptor === descriptor);
    if (measurement) {
      measurement.setHighlighting(highlighted);
    }
  }

  public setViewModelVisibility(descriptor: string, isVisible: boolean, attachedViewModel?: IBaseViewModel): void {
    const viewModel: IBaseViewModel | undefined = this.getActualViewModels().find(
      (vm: IBaseViewModel): boolean => vm.descriptor === descriptor
    );

    if (!viewModel) {
      return;
    }

    viewModel.setVisibility(isVisible);
    this._onVisibilityStateChanged(viewModel, isVisible);

    if (attachedViewModel) {
      attachedViewModel.setVisibility(isVisible);
      this._onVisibilityStateChanged(attachedViewModel, isVisible);
    }
  }

  public setViewModelSelection(descriptor: string, isSelected: boolean, attachedViewModel?: IBaseViewModel): void {
    const viewModel: IBaseViewModel | undefined = this.getActualViewModels().find(
      (vm: IBaseViewModel): boolean => vm.descriptor === descriptor
    );

    if (!viewModel) {
      return;
    }

    if (viewModel.isSelected === isSelected && attachedViewModel?.isSelected === isSelected) {
      return;
    }

    this._unselectAllViewModels();

    viewModel.setSelection(isSelected);
    this._onSelectionStateChanged(viewModel, isSelected);

    if (attachedViewModel) {
      attachedViewModel.setSelection(isSelected);
      this._onSelectionStateChanged(attachedViewModel, isSelected);
    }
  }

  private _getEditableViewModels(descriptor: string, useAttachments: boolean): Array<IBaseViewModel> {
    const allViewModels = this.getActualViewModels();

    const viewModel = allViewModels.find((vm) => vm.descriptor === descriptor);
    if (!viewModel) {
      return [];
    }

    if (useAttachments) {
      const tunableViewModels = viewModel.attachments
        .map((descr) => {
          const vm = allViewModels.find((vm) => vm.descriptor === descr);
          if (vm?.isPickable !== undefined && vm.isTunable) {
            return vm;
          } else {
            return undefined;
          }
        })
        .filter((vm) => vm !== undefined);

      if (tunableViewModels.length > 0) {
        return tunableViewModels;
      }
    }

    if (viewModel.isPickable && viewModel.isTunable) {
      return [viewModel];
    }

    return [];
  }

  public setViewModelBeingEdited(descriptor: string, isBeingEdited: boolean, useAttachments: boolean): void {
    const vms = this._getEditableViewModels(descriptor, useAttachments);
    vms.forEach((vm) => {
      vm.setBeingEdited(isBeingEdited);
    });
    if (isBeingEdited && vms.length > 0)
      this.setViewModelSelection(descriptor, true, vms[0].descriptor !== descriptor ? vms[0] : undefined);
  }

  public isViewModelBeingEdited(descriptor: string, useAttachments: boolean): boolean | undefined {
    return this._getEditableViewModels(descriptor, useAttachments).at(0)?.isBeingEdited;
  }

  public getActualViewModels(): Array<IBaseViewModel> {
    const objects = this._viewsService.currentView?.getObjects() as Array<IBaseViewModel>;
    const measurements = this._viewsService.currentView?.getMeasurements() as Array<IBaseViewModel>;
    return [...objects, ...measurements];
  }

  public locateMeasurementByDescriptor(descriptor: string, excludedSlice?: SliceType): void {
    const viewModel = this._viewsService.currentView?.getMeasurements().find((meas) => meas.descriptor === descriptor);

    if (!viewModel) return;

    const locatable = this._getLocatable(descriptor);
    if (locatable) {
      const location = locatable.getLocation();
      if (!location) {
        // [chebotarev]: originally it was 'throw' here. TODO: check the logic
        console.warn(
          'Measurement expected to contain 3d location information, while it is absent. Check Algorithm builder.'
        );
      } else {
        this._locateObject(
          ViewPortKind.ImageSlice,
          viewModel.syncSliceDescriptor,
          location,
          viewModel.zoomFactor,
          excludedSlice
        );
      }
    }
  }

  public locateIndicationByDescriptor(descriptor: string, excludedSlice?: SliceType): void {
    const location = this._getIndicationLocation(descriptor);
    if (location) {
      if (location.normal === undefined) {
        this._imagesService.syncImages(location.origin);
      } else {
        const resolved = this._imagesService.resolveClosestOrientation(location.normal);
        this._imagesService.syncSliceWithPlane(
          resolved.sliceType,
          location.origin,
          resolved.adjustedVectorZ,
          resolved.adjustedVectorY,
          excludedSlice
        );
      }
      if (excludedSlice === undefined) {
        for (const vpm of this._viewController.getRegisteredViewPorts(ViewPortKind.ImageSlice)) {
          vpm.getCameraController().ensurePointsAreVisible(location.points, 0.1);
        }
      }
    }
  }

  public locatePointByDescriptor(descriptor: string, excludedSlice?: SliceType): void {
    const viewModel = this._viewsService.currentView?.getObjects().find((obj) => obj.descriptor === descriptor);

    if (!viewModel) return;

    const pt = this._planningAlgorithm.getValue(viewModel.descriptor, DT.Point)?.value;
    if (pt) {
      if (viewModel.syncSliceDescriptor) {
        const descriptor = viewModel.attachments.at(0);
        if (descriptor) {
          const location = this._planningAlgorithm.getValue(descriptor, DT.RegionOfInterest)?.value;
          if (location) {
            this._locateObject(
              ViewPortKind.ImageSlice,
              viewModel.syncSliceDescriptor,
              {
                ptO: pt,
                dirZ: location.dirZ,
                dirY: location.dirY,
                minCorner: location.minCorner,
                maxCorner: location.maxCorner
              },
              viewModel.zoomFactor,
              excludedSlice
            );
            return;
          }
        }
      }
      this._imagesService.syncImages(pt);
    }
  }

  private _locateObject(
    viewPortKind: ViewPortKind,
    syncSlice: SliceType,
    location: Location,
    zoomFactor: number,
    excludedSlice?: SliceType
  ): void {
    this._imagesService.syncSliceWithPlane(
      syncSlice,
      location.ptO,
      location.dirZ,
      location.dirY,
      excludedSlice,
      excludedSlice !== undefined
    );
    if (excludedSlice === undefined) {
      for (const vpm of this._viewController.getRegisteredViewPorts(viewPortKind)) {
        vpm.fitCameraToBox(location.minCorner, location.maxCorner, location.dirZ, location.dirY, zoomFactor);
      }
    }
  }

  public locatePlaneByDescriptor(descriptor: string, syncSlice: SliceType, excludedSlice?: SliceType): void {
    const plane = this._planningAlgorithm.getValue(descriptor, DT.Plane);
    if (plane) {
      const orientation = coronalOrientationFromDirection(plane.value.planeEq.normal);
      this._imagesService.syncSliceWithPlane(
        syncSlice,
        plane.value.origin,
        orientation.orientationZ,
        orientation.orientationY,
        excludedSlice
      );
    }
  }

  public syncMeasurementByDescriptor(descriptor: string, point?: Vector3): void {
    const measurement = this._viewsService.currentView
      ?.getMeasurements()
      .find((meas) => meas.descriptor === descriptor);

    if (!measurement) return;

    const locatable = this._getLocatable(descriptor);
    if (locatable) {
      let syncPoint = point;
      if (!syncPoint) {
        const location = locatable.getLocation();
        if (!location) {
          // [chebotarev]: originally it was 'throw' here. TODO: check the logic
          console.warn(
            'Measurement expected to contain 3d location information, while it is absent. Check Algorithm builder.'
          );
        } else {
          syncPoint = location.ptO;
        }
      }
      if (syncPoint) {
        this._imagesService.requestSliceByPoint(measurement.syncSliceDescriptor, syncPoint);
      }
    }
  }

  private _unselectAllViewModels(): void {
    this.getActualViewModels().forEach((vm: IBaseViewModel) => {
      vm.setSelection(false);
    });
  }

  private _onVisibilityStateChanged(vm: IBaseViewModel, isVisible: boolean): void {
    if (!isVisible && vm.isSelected) {
      vm.setSelection(false);
    }
  }

  private _onSelectionStateChanged(vm: IBaseViewModel, isSelected: boolean): void {
    if (isSelected && !vm.isVisible) {
      vm.setVisibility(true);
    }
  }

  private _getLocatable(descriptor: string): ILocatable | undefined {
    const type: Type | undefined = this._planningAlgorithm.getType(descriptor);
    if (type == DT.DistanceMeasurement.Type) {
      return this._planningAlgorithm.getValue(descriptor, DT.DistanceMeasurement);
    }

    if (type == DT.AreaMeasurement.Type) {
      return this._planningAlgorithm.getValue(descriptor, DT.AreaMeasurement);
    }

    if (type == DT.AngleMeasurement.Type) {
      return this._planningAlgorithm.getValue(descriptor, DT.AngleMeasurement);
    }

    return undefined;
  }

  private _getIndicationLocation(
    descriptor: string
  ): { points: Array<THREE.Vector3>; origin: THREE.Vector3; normal?: THREE.Vector3 } | undefined {
    const type: Type | undefined = this._planningAlgorithm.getType(descriptor);

    const _getIndicationModelPoints = <T extends IValue & IIndicationModel>(
      descriptor: string,
      constructor: ValueConstructor<T>,
      ptsNum: number
    ): Array<THREE.Vector3> | undefined => {
      const indicationModel = this._planningAlgorithm.getValue(descriptor, constructor);
      const pts = indicationModel?.getPoints();
      if (pts?.length !== ptsNum || pts?.every((pt) => pt !== undefined) !== true) {
        return undefined;
      }
      return pts;
    };

    if (type == AngleModel.Type) {
      const points = _getIndicationModelPoints(descriptor, AngleModel, 3);
      if (!points) {
        return undefined;
      }
      const dir1 = points[0].clone().sub(points[1]).normalize();
      const dir2 = points[2].clone().sub(points[1]).normalize();
      return {
        points: points,
        origin: points[1],
        normal: dir2.clone().cross(dir1).normalize()
      };
    } else if (type == DistanceModel.Type) {
      const points = _getIndicationModelPoints(descriptor, DistanceModel, 2);
      if (!points) {
        return undefined;
      }
      return {
        points: points,
        origin: points[0],
        normal: points[1].clone().sub(points[0]).normalize()
      };
    } else if (type == AnnotationModel.Type) {
      const points = _getIndicationModelPoints(descriptor, AnnotationModel, 1);
      if (!points) {
        return undefined;
      }
      return {
        points: points,
        origin: points[0]
      };
    } else if (type == DT.SplineMeasurement.Type) {
      const meas = this._planningAlgorithm.getValue(descriptor, DT.SplineMeasurement);
      if (!meas || meas.splinePts.length < 3) {
        return undefined;
      }
      return {
        points: meas.splinePts,
        origin: meas.splinePts[0],
        normal: meas.normal
      };
    } else if (type == DT.AreaMeasurement.Type) {
      const meas = this._planningAlgorithm.getValue(descriptor, DT.AreaMeasurement);
      if (!meas || meas.splinePts.length < 3) {
        return undefined;
      }
      return {
        points: meas.splinePts,
        origin: meas.center,
        normal: meas.normal
      };
    }

    return undefined;
  }
}
